import { environment } from '../configuration/env';
import { paths } from '../types/swagger';

type ParamValue = string | number | boolean;

type ParamKeys<S extends string> = S extends `${string}{${infer P}}${infer R}`
  ? P | ParamKeys<R>
  : never;

type ParamsFor<S extends string> = Record<ParamKeys<S>, ParamValue>;

/**
 * Builds a complete endpoint URL by replacing path parameters with their corresponding values
 * and appending the base API path.
 *
 * @template T - A key from the `paths` object, representing the endpoint path.
 *
 * @param {T} path - The endpoint path, which may contain placeholders in the form `{param}`.
 * @param {ParamsFor<T>} [params] - An optional object containing key-value pairs for replacing
 * placeholders in the path. The keys must match the placeholders in the path.
 * @param {string} [basePath=environment.VITE_APP_HOST_API_BASE] - The base API URL. Defaults to
 * the value of `environment.VITE_APP_HOST_API_BASE`.
 *
 * @returns {string} - The fully constructed endpoint URL with placeholders replaced by their
 * corresponding values.
 *
 * @example
 * // Assuming `paths` contains the following:
 * // {
 * //   "/users/{userId}/posts/{postId}": {}
 * // }
 *
 * const url = buildEndpointUrl(
 *   "/users/{userId}/posts/{postId}",
 *   { userId: 123, postId: 456 }
 * );
 * console.log(url); // Outputs: "https://api.example.com/users/123/posts/456"
 *
 * @example
 * // Using a custom base path:
 * const url = buildEndpointUrl(
 *   "/users/{userId}",
 *   { userId: 789 },
 *   "https://custom-api.example.com"
 * );
 * console.log(url); // Outputs: "https://custom-api.example.com/users/789"
 *
 * @dependency - This function depends on the `paths` type from `@/swagger/types/slash` and the
 * `environment` object from `../configuration/env`.
 */
export const buildEndpointUrl = <T extends keyof paths>(
  path: T,
  params: ParamsFor<T>,
  basePath = environment.VITE_APP_HOST_API_BASE,
): string => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return `${basePath}${path.replace(/{([^}]+)}/g, (_, key: string) => {
    const value = params?.[key as keyof typeof params];
    return value ? `${value}` : '';
  })}`;
};
